import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  api,
  ApiError,
  Status,
  type ComplianceDetail,
  type DocumentSummary,
  type UploadResult,
} from '../api/client'
import { openDocument, saveDocument } from '../api/openBlob'
import { invalidateFleet } from '../api/invalidate'
import './ObligationDocuments.css'

/**
 * The scans behind one obligation's date, shown inside that obligation's row.
 *
 * Deliberately not a separate documents panel. The certificate and the date are
 * two halves of one fact — the document is the evidence the register's value
 * rests on, and when the expiry printed on the paper disagrees with the expiry
 * in the register, that disagreement is the finding this feature exists to
 * surface. A parallel list would list every obligation twice and put the two
 * halves on different parts of the page.
 *
 * Whether this appears at all is driven by `acceptsDocument` from the reference
 * data, decided by the caller — Permit (4) and Goods Tax (7) simply have no
 * attach control.
 */
export function ObligationDocuments({
  vehicleId,
  detail,
  documents,
  open,
  onClose,
}: {
  vehicleId: number
  detail: ComplianceDetail
  documents: DocumentSummary[]
  open: boolean
  onClose: () => void
}) {
  if (!open) return null

  return (
    <div className="odocs">
      {documents.length === 0 ? (
        <p className="odocs__empty">
          No certificate attached yet. Attaching one is also how the date gets
          corrected — read the expiry off the scan and it becomes the register's
          value, with the scan behind it.
        </p>
      ) : (
        <ul className="doclist">
          {documents.map((doc) => (
            <DocumentRow key={doc.complianceDocumentId} vehicleId={vehicleId} doc={doc} />
          ))}
        </ul>
      )}

      <UploadForm vehicleId={vehicleId} detail={detail} onDone={onClose} />
    </div>
  )
}

function DocumentRow({ vehicleId, doc }: { vehicleId: number; doc: DocumentSummary }) {
  const [confirming, setConfirming] = useState(false)
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: () => api.deleteDocument(vehicleId, doc.complianceDocumentId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['documents', vehicleId] })
      await queryClient.invalidateQueries({ queryKey: ['activity', vehicleId] })
    },
  })

  return (
    <li className={`doc${doc.isCurrent ? ' doc--current' : ''}`}>
      <div className="doc__main">
        {/* A link, not a fetch: the browser's own PDF and image viewers handle
            this far better than anything worth building here. The URL points at
            the API, so no storage path ever reaches the page. */}
        <button
          type="button"
          className="doc__name"
          onClick={() => void openDocument(vehicleId, doc.complianceDocumentId!)}
        >
          {doc.fileName}
        </button>

        <div className="doc__meta mono">
          {doc.isCurrent && <span className="doc__current">backs the date</span>}
          {doc.documentExpiryDate && <span>reads {formatDate(doc.documentExpiryDate)}</span>}
          <span>{formatSize(doc.sizeBytes)}</span>
          <span>{doc.uploadedByName ?? doc.uploadedBy}</span>
          <span>{formatDate(doc.uploadedAtUtc)}</span>
        </div>

        {doc.notes && <p className="doc__notes">{doc.notes}</p>}

        {remove.error && (
          <p className="editor__error" role="alert">
            {(remove.error as Error).message}
          </p>
        )}
      </div>

      <div className="doc__actions">
        <button
          type="button"
          className="prow__edit"
          onClick={() =>
            void saveDocument(vehicleId, doc.complianceDocumentId!, doc.fileName ?? 'certificate')
          }
        >
          Save
        </button>

        {!confirming ? (
          <button type="button" className="prow__edit" onClick={() => setConfirming(true)}>
            Remove
          </button>
        ) : (
          <>
            <button
              type="button"
              className="prow__edit prow__edit--danger"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? '…' : 'Confirm'}
            </button>
            <button type="button" className="prow__edit" onClick={() => setConfirming(false)}>
              Keep
            </button>
          </>
        )}
      </div>
    </li>
  )
}

function UploadForm({
  vehicleId,
  detail,
  onDone,
}: {
  vehicleId: number
  detail: ComplianceDetail
  onDone: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [expiry, setExpiry] = useState('')
  const [notes, setNotes] = useState('')
  const [apply, setApply] = useState(false)
  const [result, setResult] = useState<UploadResult | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const queryClient = useQueryClient()

  const upload = useMutation({
    mutationFn: () =>
      api.uploadDocument(vehicleId, detail.complianceTypeId!, file!, {
        documentExpiryDate: expiry || undefined,
        notes: notes || undefined,
        applyExpiryToRegister: apply,
      }),
    onSuccess: async (uploaded) => {
      setResult(uploaded)
      setFile(null)
      setExpiry('')
      setNotes('')
      setApply(false)
      if (inputRef.current) inputRef.current.value = ''

      await queryClient.invalidateQueries({ queryKey: ['documents', vehicleId] })
      // The register and the activity feed both move when a date is applied, so
      // both are refreshed rather than only the document list.
      await queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      await invalidateFleet(queryClient)
      await queryClient.invalidateQueries({ queryKey: ['activity', vehicleId] })
    },
  })

  const registerReads =
    detail.status === Status.Known && detail.expiryDate
      ? formatDate(detail.expiryDate)
      : describeState(detail.status)

  // Only offer to correct the register when the two actually differ. Offering it
  // when they already agree invites a pointless write that flags the value as
  // hand-corrected for no reason.
  const differs = Boolean(expiry) && expiry !== (detail.expiryDate ?? '')

  const duplicate = upload.error instanceof ApiError && upload.error.status === 409

  return (
    <form
      className="upload"
      onSubmit={(e) => {
        e.preventDefault()
        if (file) upload.mutate()
      }}
    >
      <label className="editor__field">
        <span className="eyebrow">Scan or photograph</span>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null)
            setResult(null)
          }}
        />
        <span className="upload__hint">
          PDF, JPEG or PNG, up to 10 MB. The file's contents are checked, not its name.
        </span>
      </label>

      <label className="editor__field">
        <span className="eyebrow">Expiry printed on it</span>
        <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        <span className="upload__hint">
          Optional. The register currently reads <b>{registerReads}</b>.
        </span>
      </label>

      {differs && (
        <label className="upload__apply">
          <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} />
          <span>
            Correct the register to <b>{formatDate(expiry)}</b>. It is marked hand-corrected,
            so a later import or RTO sync will not overwrite it.
          </span>
        </label>
      )}

      <label className="editor__field">
        <span className="eyebrow">Note</span>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="optional"
        />
      </label>

      {duplicate && (
        <p className="editor__conflict" role="alert">
          {(upload.error as Error).message}
        </p>
      )}

      {upload.error && !duplicate && (
        <p className="editor__error" role="alert">
          {(upload.error as Error).message}
        </p>
      )}

      {result?.register && (
        <p className={result.register.applied ? 'editor__note' : 'upload__disagree'} role="status">
          {result.register.message}
        </p>
      )}

      <div className="editor__actions">
        <button type="submit" className="btn btn--primary" disabled={!file || upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Attach'}
        </button>
        <button type="button" className="btn" onClick={onDone} disabled={upload.isPending}>
          Done
        </button>
      </div>
    </form>
  )
}

function describeState(status: number | undefined): string {
  switch (status) {
    case Status.NoExpiry:
      return 'no expiry'
    case Status.NotApplicable:
      return 'not applicable'
    case Status.Known:
      return 'a date'
    default:
      return 'unknown'
  }
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—'

  // Date-only values carry no timezone; parsing them as local avoids the
  // one-day shift that new Date('2026-10-16') produces west of UTC.
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)

  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function formatSize(bytes: number | undefined): string {
  if (!bytes) return '—'
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`
}
